import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import api from '../api/axiosConfig';
import { SettingsContext } from './SettingsContext';
// 🟢 NEW: Import Auth Context
import { useAuth } from './AuthContext';

// 1. Create the Context
export const CurrencyContext = createContext();

// 2. Create a Safety Hook to prevent "is not a function" errors
export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    // If the provider is missing, return a dummy function so the app doesn't crash
    return {
      displayMoney: (amount) => `₹${Number(amount || 0).toLocaleString('en-IN')}`,
      formatMoney: (amount) => `₹${Number(amount || 0).toLocaleString('en-IN')}`,
      activeCurrency: { symbol: '₹', code: 'INR' },
      loading: false
    };
  }
  return context;
};

// 🟢 FALLBACK DATA: Prevents the UI from ever going blank
const FALLBACK_CURRENCIES = [
  { id: 1, code: 'USD', name: 'US Dollar', symbol: '$', exchange_rate: 1.0, locale: 'en-US' },
  { id: 2, code: 'INR', name: 'Indian Rupee', symbol: '₹', exchange_rate: 83.0, locale: 'en-IN' },
  { id: 3, code: 'EUR', name: 'Euro', symbol: '€', exchange_rate: 0.92, locale: 'de-DE' },
  { id: 4, code: 'GBP', name: 'British Pound', symbol: '£', exchange_rate: 0.79, locale: 'en-GB' }
];

export const CurrencyProvider = ({ children }) => {
  const { currencyId, saveSettings } = useContext(SettingsContext);
  
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [activeCurrency, setActiveCurrency] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // 🟢 NEW: Get the current user
  const { user } = useAuth();

  // 1. Fetch live DB currencies
  const fetchCurrencies = useCallback(async () => {
    try {
      const res = await api.get('/currencies');
      const data = Array.isArray(res.data) && res.data.length > 0 ? res.data : (res.data?.data || FALLBACK_CURRENCIES);
      setAvailableCurrencies(data);

      // Auto-select active currency based on settings or default to INR/First
      const saved = data.find(c => c.id === parseInt(currencyId)) || data.find(c => c.code === 'INR') || data[0];
      setActiveCurrency(saved);
    } catch (error) {
      console.error("Currency Fetch Error:", error);
      setAvailableCurrencies(FALLBACK_CURRENCIES);
      setActiveCurrency(FALLBACK_CURRENCIES[1]); // Default to INR
    } finally {
      setLoading(false);
    }
  }, [currencyId]);

  // 🟢 FIX: Only fetch if user is logged in to avoid unnecessary 403s
  useEffect(() => {
    if (user && user.id) {
      fetchCurrencies();
    } else {
      setAvailableCurrencies(FALLBACK_CURRENCIES);
      setActiveCurrency(FALLBACK_CURRENCIES[1]);
      setLoading(false);
    }
  }, [user, fetchCurrencies]);

  // 2. Handle Manual Change
  const handleSetCurrency = (currency) => {
    setActiveCurrency(currency);
    if (saveSettings) saveSettings({ currencyId: currency.id });
  };

  // 3. THE FIX: Formatting Logic
  // This is the function BudgetPage was looking for
  const formatMoney = (amount, isBaseCurrency = true) => {
    try {
      let safeAmount = parseFloat(amount);
      if (isNaN(safeAmount)) safeAmount = 0;

      // Logic to handle exchange rates if needed
      if (isBaseCurrency && activeCurrency && activeCurrency.exchange_rate && activeCurrency.code !== 'USD') {
          // If your DB stores everything in USD, multiply by the rate
          // safeAmount = safeAmount * parseFloat(activeCurrency.exchange_rate);
      }

      return new Intl.NumberFormat(activeCurrency?.locale || 'en-IN', {
        style: 'currency',
        currency: activeCurrency?.code || 'INR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(safeAmount);
      
    } catch (error) {
      return `${activeCurrency?.symbol || '₹'}${amount || 0}`;
    }
  };

  // 4. Cross-Currency Math Converter
  const convertAmount = (amount, fromCurrencyCode) => {
    if (!activeCurrency || !availableCurrencies.length) return amount;
    const fromCurrency = availableCurrencies.find(c => c.code === fromCurrencyCode);
    if (!fromCurrency) return amount;

    const fromRate = fromCurrency.exchange_rate;
    const toRate = activeCurrency.exchange_rate;
    return (amount / fromRate) * toRate;
  };

  return (
    <CurrencyContext.Provider value={{ 
      activeCurrency, 
      setActiveCurrency: handleSetCurrency, 
      currencies: availableCurrencies,
      loading,
      formatMoney,         // Original name
      displayMoney: formatMoney, // FIX: Alias added so BudgetPage.jsx works
      convertAmount,
      refresh: fetchCurrencies
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};